// Camera help (src/live/camerahelp.ts): what the phone's camera sees of the sky, worked out on the phone one video frame
// at a time. These are plain sums with no page attached, so they can be checked on made-up frames (tests/skycam.test.ts).
// Nothing here keeps a frame: each is measured and let go.
//
// A frame gives three things: the sky's level and noise across it (the background), the points of light standing out
// from it (stars and planets, and anything else small and bright), and how patchy the sky is (lit cloud comes in
// patches; a light-polluted clear sky brightens smoothly towards the horizon). With stars the camera has matched, the
// sky's brightness follows too, in the units a Sky Quality Meter reads (skyBrightnessFrom). The numbers marked "tune"
// are first guesses for phone video at night, to be checked on real phones outdoors.

/** One frame as brightness alone: 0 to 255 per pixel, row by row, as the camera's video encodes it (not linear light). */
export interface Frame {
  w: number;
  h: number;
  px: Uint8Array;
}

/** The frame's brightness from the video's red, green and blue (Rec. 709 weights), written into `out` if given. */
export function lumaOf(rgba: ArrayLike<number>, w: number, h: number, out: Uint8Array = new Uint8Array(w * h)): Frame {
  for (let i = 0, j = 0; i < w * h; i++, j += 4) out[i] = (54 * rgba[j] + 183 * rgba[j + 1] + 19 * rgba[j + 2] + 128) >> 8;
  return { w, h, px: out };
}

/** Each video value as light, from 0 to 1: the sRGB curve undone. Phone video adds a tone curve of its own, so this is
 *  close rather than exact. */
export const LINEAR = (() => {
  const t = new Float32Array(256);
  for (let v = 0; v < 256; v++) {
    const c = v / 255;
    t[v] = c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }
  return t;
})();

/** Light at a video value that may fall between whole values (a median, say). */
export function linear(v: number): number {
  if (!(v > 0)) return 0;
  if (v >= 255) return 1;
  const i = Math.floor(v), f = v - i;
  return LINEAR[i] * (1 - f) + LINEAR[i + 1] * f;
}

// ---------- Smoothing ----------
/**
 * The frame blurred by a 3×3 binomial kernel (1 2 1 by 1 2 1), in sixteenths of a video value. A star's light is
 * spread over a few pixels, so blurring it this little keeps most of its peak while noise, which differs pixel by pixel,
 * drops to about a third: the points worth measuring stand out, and a single noisy pixel no longer does. Edges repeat
 * their last pixel.
 */
export function smooth(f: Frame, out = new Uint16Array(f.w * f.h)): Uint16Array {
  const { w, h, px } = f;
  if (w < 2 || h < 2) {
    for (let i = 0; i < w * h; i++) out[i] = 16 * px[i];
    return out;
  }
  const row = new Uint16Array(w * h);
  for (let y = 0; y < h; y++) {
    const o = y * w;
    row[o] = 3 * px[o] + px[o + 1];
    for (let x = 1; x < w - 1; x++) row[o + x] = px[o + x - 1] + 2 * px[o + x] + px[o + x + 1];
    row[o + w - 1] = px[o + w - 2] + 3 * px[o + w - 1];
  }
  for (let y = 0; y < h; y++) {
    const up = (y ? y - 1 : 0) * w, me = y * w, down = (y < h - 1 ? y + 1 : y) * w;
    for (let x = 0; x < w; x++) out[me + x] = row[up + x] + 2 * row[me + x] + row[down + x];
  }
  return out;
}

// ---------- The background ----------
/** The sky's level and noise, tile by tile, in video values. */
export interface Background {
  /** Tile size (px), and how many tiles across and down. */
  tile: number;
  nx: number;
  ny: number;
  /**
   * Each tile's sky level (the median of the smoothed frame) and noise there (one standard deviation, from differences
   * between smoothed pixels three apart: see tileNoise), after a 3×3 median across tiles, so that a bright light filling
   * one tile is not taken for sky.
   */
  level: Float32Array;
  noise: Float32Array;
  /** Each tile's own median before that, for the sky's texture. */
  raw: Float32Array;
  /** The darkest the frame goes (its 0.5th percentile): the camera's black. */
  black: number;
  /** The smoothed frame the levels were read from, for finding points of light in. */
  smoothed: Uint16Array;
}
/** The background without its smoothed frame: what the sky's level and texture need, small enough to send between threads. */
export type SkyBackground = Omit<Background, 'smoothed'>;

/** Noise reduction in phone video can leave almost none: a floor (video values, smoothed), so a threshold in noise levels
 *  still means something. */
const NOISE_MIN = 0.25;

/** The value below which a share `q` of a histogram's `n` counts lie, between whole values. */
function percentile(hist: Uint32Array, n: number, q: number): number {
  const target = q * n;
  let cum = 0;
  for (let v = 0; v < hist.length; v++) {
    const c = hist[v];
    if (c && cum + c >= target) return v - 0.5 + (target - cum) / c;
    cum += c;
  }
  return hist.length - 1;
}

/** The median of a short list (sorts it). */
function median(xs: number[]): number {
  xs.sort((a, b) => a - b);
  const m = xs.length >> 1;
  return xs.length % 2 ? xs[m] : (xs[m - 1] + xs[m]) / 2;
}

const diffs = new Int16Array(1024);
/** The median absolute deviation of the first `n` differences in `diffs`, about their own median. */
function mad(n: number): number {
  const d = diffs.subarray(0, n).sort();
  const mid = d[n >> 1];
  for (let k = 0; k < n; k++) d[k] = Math.abs(d[k] - mid);
  d.sort();
  return d[n >> 1];
}

/**
 * The noise in one tile of the smoothed frame, in sixteenths: from differences between pixels three apart, across and
 * down (at three apart the 3×3 smoothing no longer ties them, so each difference has twice the noise's variance). Each
 * set is taken about its own median, so a steady glow across the tile, which shifts them all alike, does not count as
 * noise; and the median deviation is blind to the few pixels of a star.
 */
function tileNoise(sm: Uint16Array, w: number, x0: number, y0: number, x1: number, y1: number): number {
  let n = 0;
  for (let y = y0; y < y1; y += 2) for (let x = x0; x + 3 < x1 && n < diffs.length; x += 2) diffs[n++] = sm[y * w + x + 3] - sm[y * w + x];
  const across = n > 8 ? mad(n) : NaN;
  n = 0;
  for (let y = y0; y + 3 < y1; y += 2) for (let x = x0; x < x1 && n < diffs.length; x += 2) diffs[n++] = sm[(y + 3) * w + x] - sm[y * w + x];
  const down = n > 8 ? mad(n) : NaN;
  const m = Number.isNaN(across) ? down : Number.isNaN(down) ? across : (across + down) / 2;
  return (1.4826 * m) / Math.SQRT2;
}

/** The median of the first `n` (up to nine) numbers in `nine` (sorts them there). */
function median9(nine: Float64Array, n: number): number {
  const d = nine.subarray(0, n).sort();
  return n % 2 ? d[n >> 1] : (d[(n >> 1) - 1] + d[n >> 1]) / 2;
}

/**
 * The sky's level and noise across a frame, tile by tile. The level is the median of every other smoothed pixel each way
 * (the smoothed frame changes little from one pixel to the next, so that loses nothing, and sorting a quarter of the
 * pixels is quick); the noise comes from tileNoise. The camera's black is read from every third pixel.
 */
export function background(f: Frame, tile = 32): Background {
  const sm = smooth(f);
  const nx = Math.ceil(f.w / tile), ny = Math.ceil(f.h / tile);
  const raw = new Float32Array(nx * ny), rawNoise = new Float32Array(nx * ny);
  const sample = new Uint16Array(((tile + 1) >> 1) ** 2), all = new Uint32Array(256);
  let counted = 0;
  for (let i = 0; i < f.px.length; i += 3, counted++) all[f.px[i]]++;
  for (let ty = 0; ty < ny; ty++) {
    for (let tx = 0; tx < nx; tx++) {
      const x0 = tx * tile, y0 = ty * tile, x1 = Math.min(f.w, x0 + tile), y1 = Math.min(f.h, y0 + tile);
      let n = 0;
      for (let y = y0; y < y1; y += 2) for (let x = x0; x < x1; x += 2) sample[n++] = sm[y * f.w + x];
      const sorted = sample.subarray(0, n).sort();
      const mid = n % 2 ? sorted[n >> 1] : (sorted[(n >> 1) - 1] + sorted[n >> 1]) / 2;
      raw[ty * nx + tx] = mid / 16;
      const nz = tileNoise(sm, f.w, x0, y0, x1, y1);
      rawNoise[ty * nx + tx] = Math.max(NOISE_MIN, (Number.isNaN(nz) ? mid - sorted[Math.floor(0.1587 * n)] : nz) / 16);
    }
  }
  const level = new Float32Array(nx * ny), noise = new Float32Array(nx * ny);
  const a = new Float64Array(9), b = new Float64Array(9);
  for (let ty = 0; ty < ny; ty++) {
    for (let tx = 0; tx < nx; tx++) {
      let n = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const x = tx + dx, y = ty + dy;
          if (x < 0 || y < 0 || x >= nx || y >= ny) continue;
          a[n] = raw[y * nx + x];
          b[n++] = rawNoise[y * nx + x];
        }
      }
      level[ty * nx + tx] = median9(a, n);
      noise[ty * nx + tx] = median9(b, n);
    }
  }
  return { tile, nx, ny, level, noise, raw, black: Math.max(0, percentile(all, counted, 0.005)), smoothed: sm };
}

/** The sky's level and noise (smoothed) at a pixel, between the tiles' centres; beyond the outer centres, carried on as
 *  the last two tiles slope (a glow keeps brightening to the frame's edge), the noise held. */
export function skyAt(b: SkyBackground, x: number, y: number): { level: number; noise: number } {
  const gx = Math.min(Math.max(x / b.tile - 0.5, -0.5), b.nx - 0.5), gy = Math.min(Math.max(y / b.tile - 0.5, -0.5), b.ny - 0.5);
  const x0 = b.nx < 2 ? 0 : Math.min(Math.max(Math.floor(gx), 0), b.nx - 2), y0 = b.ny < 2 ? 0 : Math.min(Math.max(Math.floor(gy), 0), b.ny - 2);
  const x1 = Math.min(x0 + 1, b.nx - 1), y1 = Math.min(y0 + 1, b.ny - 1);
  const fx = x1 === x0 ? 0 : gx - x0, fy = y1 === y0 ? 0 : gy - y0;
  const at = (a: Float32Array, fx: number, fy: number) =>
    (a[y0 * b.nx + x0] * (1 - fx) + a[y0 * b.nx + x1] * fx) * (1 - fy) + (a[y1 * b.nx + x0] * (1 - fx) + a[y1 * b.nx + x1] * fx) * fy;
  return { level: at(b.level, fx, fy), noise: at(b.noise, clamp01(fx), clamp01(fy)) };
}
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** The sky right round a point: the median of the smoothed frame in a ring 5 to 6 px out (video values). A star's own
 *  light is all inside it; a lamp's halo or the edge of a cloud is not. */
function ringSky(sm: Uint16Array, w: number, h: number, x: number, y: number): number {
  const vals: number[] = [];
  for (let dy = -6; dy <= 6; dy++) {
    for (let dx = -6; dx <= 6; dx++) {
      const r2 = dx * dx + dy * dy;
      if (r2 < 25 || r2 > 40) continue;
      const xx = x + dx, yy = y + dy;
      if (xx >= 0 && yy >= 0 && xx < w && yy < h) vals.push(sm[yy * w + xx]);
    }
  }
  return vals.length ? median(vals) / 16 : 0;
}

// ---------- Points of light ----------
/** A point of light standing out from the sky. */
export interface Source {
  /** Its centre (px, to a fraction of a pixel). */
  x: number;
  y: number;
  /** Its light above the sky, summed (linear, as LINEAR), and its brightest pixel above the sky (video values). */
  flux: number;
  peak: number;
  /** How far it spreads: the radius (px) within which it is brighter than half its peak. */
  size: number;
  /** How far it stands out of the noise (in the smoothed frame). */
  snr: number;
  /** Its middle is at the camera's limit (250 and over), so its light is only a lower bound. */
  saturated: boolean;
}

/** A light too big to be a star (a street lamp, the Moon): points near it are its glow, not stars. */
export interface Glare {
  x: number;
  y: number;
  r: number;
}

export const DETECT = {
  /** A point must stand this many noise levels above the sky, in the smoothed frame (tune). */
  sigma: 5,
  /** And at least this far above it (video values, smoothed), as noise reduction can leave too little noise to judge
   *  by (tune). */
  minExcess: 4,
  /** Measured within this many pixels of its peak. */
  reach: 4,
  /** Spread wider than this (px, the radius at half its peak) is not a star but a lamp, a lit window or a glow (tune). */
  maxSize: 2.5,
  /** Clipped pixels (250 and over) in one patch: more than this is a light, not a star or planet (tune). */
  satArea: 30,
  /** Fainter points this close to a clipped one (px) are its own glow. */
  satGlow: 7,
  /** The brightest this many are kept. */
  keep: 40,
};

/** A patch of clipped pixels (250 and over) joined side to side, from (x, y): its size and middle, up to `cap` pixels. */
function clippedPatch(f: Frame, x: number, y: number, cap: number): { area: number; x: number; y: number } {
  const seen = new Set<number>();
  const stack = [y * f.w + x];
  let sx = 0, sy = 0;
  while (stack.length && seen.size < cap) {
    const i = stack.pop()!;
    if (seen.has(i) || f.px[i] < 250) continue;
    seen.add(i);
    const px = i % f.w, py = (i - px) / f.w;
    sx += px;
    sy += py;
    if (px > 0) stack.push(i - 1);
    if (px < f.w - 1) stack.push(i + 1);
    if (py > 0) stack.push(i - f.w);
    if (py < f.h - 1) stack.push(i + f.w);
  }
  const n = Math.max(1, seen.size);
  return { area: seen.size, x: sx / n, y: sy / n };
}

/**
 * The points of light in a frame, brightest first: each a peak of the smoothed frame at least `DETECT.sigma` noise
 * levels (and `DETECT.minExcess` values) above the sky there, then measured in the frame itself and kept if small
 * enough to be a star or planet. Clipped patches bigger than a planet's are lights (`glare`), and points in their glow,
 * or right beside a clipped star, are left out.
 */
export function findSources(f: Frame, b: Background): { sources: Source[]; glare: Glare[] } {
  const { w, h, px } = f;
  const sm = b.smoothed;
  const R = DETECT.reach;
  // No pixel below the lowest threshold of any tile can be a source: most are passed over at once.
  let floor = Infinity;
  for (let k = 0; k < b.level.length; k++) floor = Math.min(floor, 16 * (b.level[k] + Math.max(DETECT.sigma * b.noise[k], DETECT.minExcess)));
  const found: Source[] = [];
  const glare: Glare[] = [];
  for (let y = 2; y < h - 2; y++) {
    for (let x = 2; x < w - 2; x++) {
      const i = y * w + x, v = sm[i];
      if (v < floor) continue;
      // A peak: no neighbour higher, and none before it (in reading order) as high, so a flat top counts once.
      if (sm[i - w - 1] >= v || sm[i - w] >= v || sm[i - w + 1] >= v || sm[i - 1] >= v) continue;
      if (sm[i + 1] > v || sm[i + w - 1] > v || sm[i + w] > v || sm[i + w + 1] > v) continue;
      const tileSky = skyAt(b, x, y);
      if (v / 16 - tileSky.level < Math.max(DETECT.sigma * tileSky.noise, DETECT.minExcess)) continue;
      // Measured against the sky right round it where that is brighter (a lamp's halo, the edge of lit cloud).
      const sky = { level: Math.max(tileSky.level, ringSky(sm, w, h, x, y)), noise: tileSky.noise };
      const lift = v / 16 - sky.level;
      if (lift < Math.max(DETECT.sigma * sky.noise, DETECT.minExcess)) continue;
      // The brightest pixel of the frame itself next to the smoothed peak.
      let top = 0, tx = x, ty = y;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const q = px[i + dy * w + dx];
          if (q > top) [top, tx, ty] = [q, x + dx, y + dy];
        }
      }
      const excess = top - sky.level;
      const saturated = top >= 250;
      let cx: number, cy: number;
      if (saturated) {
        const patch = clippedPatch(f, tx, ty, 4 * DETECT.satArea);
        if (patch.area > DETECT.satArea) {
          glare.push({ x: patch.x, y: patch.y, r: 3 * Math.sqrt(patch.area / Math.PI) + 8 });
          continue;
        }
        [cx, cy] = [patch.x, patch.y];
      } else [cx, cy] = [tx, ty];
      // Its centre from the smoothed frame's pixels well above the sky (steadier than the frame's own; noise further out
      // would pull it about), its light from the frame's pixels near it, and its spread from those above half its peak.
      const cut = Math.max(2 * sky.noise, 0.2 * lift), half = sky.level + excess / 2, base = linear(sky.level);
      let flux = 0, above = 0;
      const ox = Math.round(cx), oy = Math.round(cy);
      for (let dy = -R; dy <= R; dy++) {
        const yy = oy + dy;
        if (yy < 0 || yy >= h) continue;
        for (let dx = -R; dx <= R; dx++) {
          const xx = ox + dx;
          if (xx < 0 || xx >= w) continue;
          const q = px[yy * w + xx];
          flux += LINEAR[q] - base;
          if (q >= half) above++;
        }
      }
      const size = Math.sqrt(above / Math.PI);
      // A clipped middle makes any star look wide at half its (clipped) peak: its patch was checked above instead.
      if (!saturated && size > DETECT.maxSize) continue;
      if (!saturated) {
        let sw = 0, sx = 0, sy = 0;
        for (let dy = -3; dy <= 3; dy++) {
          for (let dx = -3; dx <= 3; dx++) {
            const xx = x + dx, yy = y + dy;
            if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
            const wgt = sm[yy * w + xx] / 16 - sky.level - cut;
            if (wgt <= 0) continue;
            sw += wgt;
            sx += wgt * dx;
            sy += wgt * dy;
          }
        }
        if (sw) [cx, cy] = [x + sx / sw, y + sy / sw];
      }
      found.push({ x: cx, y: cy, flux: Math.max(flux, 0), peak: excess, size, snr: lift / sky.noise, saturated });
    }
  }
  found.sort((a, b) => b.flux - a.flux);
  const sources: Source[] = [];
  for (const s of found) {
    if (glare.some((g) => Math.hypot(s.x - g.x, s.y - g.y) < g.r)) continue;
    // Two peaks of one point, or the glow round a clipped star: the brighter stands for both.
    if (sources.some((t) => Math.hypot(s.x - t.x, s.y - t.y) < (t.saturated ? DETECT.satGlow : 3))) continue;
    sources.push(s);
    if (sources.length >= DETECT.keep) break;
  }
  return { sources, glare };
}

/** What one frame shows: its size, its sky and the points of light in it (src/live/camera.worker.ts sends it back). */
export interface Analysed {
  w: number;
  h: number;
  bg: SkyBackground;
  sources: Source[];
  glare: Glare[];
}

/** A frame's sky and points of light, with the smoothed frame let go. */
export function analyseFrame(f: Frame): Analysed {
  const b = background(f);
  const found = findSources(f, b);
  const { smoothed, ...bg } = b;
  void smoothed;
  return { w: f.w, h: f.h, bg, ...found };
}

// ---------- How patchy the sky is ----------
/**
 * How patchy the sky is: how far the tiles' sky levels stray from a smooth surface through them (a cubic in across and
 * down, which follows a glow brightening towards the horizon, even as video encodes it), as a share of the sky's level
 * above the camera's black (`rel`) and in video values (`abs`). The 80th percentile of the strays, so cloud over a fifth
 * of the frame or more counts, and a stray tile or two (a tree, a roof) does not. A light-polluted clear sky, or an even
 * haze, fits the surface; lit cloud comes in patches, which do not. Tiles for which `skip` says yes (below the horizon,
 * near the Moon or a light) are left out; with too few left it says nothing (NaN).
 */
export function texture(b: SkyBackground, skip?: (tx: number, ty: number) => boolean): { rel: number; abs: number; level: number } {
  const terms = (x: number, y: number) => [1, x, y, x * x, x * y, y * y, x * x * x, x * x * y, x * y * y, y * y * y];
  const T: number[][] = [], vs: number[] = [];
  for (let ty = 0; ty < b.ny; ty++) {
    for (let tx = 0; tx < b.nx; tx++) {
      if (skip?.(tx, ty)) continue;
      T.push(terms((tx + 0.5) / b.nx - 0.5, (ty + 0.5) / b.ny - 0.5));
      vs.push(b.raw[ty * b.nx + tx]);
    }
  }
  const n = 10;
  if (vs.length < 2 * n) return { rel: NaN, abs: NaN, level: NaN };
  const A = Array.from({ length: n }, () => new Array<number>(n + 1).fill(0));
  for (let k = 0; k < vs.length; k++) {
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) A[r][c] += T[k][r] * T[k][c];
      A[r][n] += T[k][r] * vs[k];
    }
  }
  const coef = solve(A);
  const strays = vs.map((v, k) => Math.abs(v - T[k].reduce((s, t, j) => s + t * coef[j], 0))).sort((a, c) => a - c);
  const abs = strays[Math.floor(0.8 * (strays.length - 1))];
  const level = median([...vs]);
  return { rel: abs / Math.max(level - b.black, 8), abs, level };
}

/** Gaussian elimination with partial pivoting on an augmented matrix; where it is singular, zero. */
function solve(A: number[][]): number[] {
  const n = A.length;
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
    [A[c], A[p]] = [A[p], A[c]];
    if (Math.abs(A[c][c]) < 1e-12) continue;
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const k = A[r][c] / A[c][c];
      for (let j = c; j <= n; j++) A[r][j] -= k * A[c][j];
    }
  }
  return A.map((row, r) => (Math.abs(row[r]) < 1e-12 ? 0 : row[n] / row[r]));
}

// ---------- The sky's brightness, from the stars ----------
/**
 * The camera's zero point from stars it has matched: the magnitude that would give a summed light of 1, from each one's
 * predicted magnitude and measured light. The median, so one odd star does not decide it. Clipped stars only give a
 * lower bound on their light and are left out, unless there is nothing else. NaN with none.
 */
export function zeroPoint(stars: { mag: number; flux: number; saturated: boolean }[]): number {
  const use = stars.some((s) => !s.saturated) ? stars.filter((s) => !s.saturated) : stars;
  const zs = use.filter((s) => s.flux > 0).map((s) => s.mag + 2.5 * Math.log10(s.flux));
  return zs.length ? median(zs) : NaN;
}

/**
 * The sky's brightness in magnitudes per square arcsecond, as a Sky Quality Meter reads it (higher is darker): from the
 * camera's zero point, the sky's level and the camera's black (video values), and the size of a pixel on the sky
 * (arcseconds across). The light in one pixel is (zero point − 2.5 log₁₀ light) bright; spread over the pixel's area,
 * each square arcsecond is 2.5 log₁₀(area) fainter. NaN where it cannot say.
 */
export function skyBrightnessFrom(zp: number, level: number, black: number, pxArcsec: number): number {
  const light = linear(level) - linear(black);
  if (!(light > 0) || !Number.isFinite(zp) || !(pxArcsec > 0)) return NaN;
  return zp - 2.5 * Math.log10(light) + 5 * Math.log10(pxArcsec);
}
